"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { LazyMotion, domAnimation, m, useReducedMotion } from "framer-motion";
import { DistrictsMarquee } from "@/components/location/DistrictsMarquee";
import type { LocationHeader126Vm } from "@/lib/location-header126";
const slideUp = {
  hidden: {
    y: 40,
    opacity: 0
  },
  visible: (i: number) => ({
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.8,
      ease: [0.215, 0.61, 0.355, 1],
      delay: i * 0.1
    }
  })
} as const;
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
} as const;
export type LocationHeader126ClientProps = LocationHeader126Vm & {
  theme?: "light" | "dark";
};
export function LocationHeader126Client(t0) {
  const $ = _c(32);
  const {
    heading,
    districts,
    firstImage,
    secondImage,
    theme: t1
  } = t0;
  const theme = t1 === undefined ? "dark" : t1;
  const prefersReduced = useReducedMotion();
  const variants = prefersReduced ? undefined : slideUp;
  const isDark = theme === "dark";
  const t2 = isDark ? "bg-secondary text-primary" : "bg-primary text-black";
  let t3;
  if ($[0] !== t2) {
    t3 = ["px-[5%] py-16 md:py-24 lg:py-28", t2];
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = t3.join(" ");
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      once: true,
      amount: 0.2
    };
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== heading || $[4] !== variants) {
    t6 = <m.h2 variants={variants} custom={0} className="mb-8 text-4xl font-medium md:mb-10 md:text-6xl lg:mb-12 lg:text-7xl">{heading}</m.h2>;
    $[3] = heading;
    $[4] = variants;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== districts || $[7] !== isDark || $[8] !== variants) {
    t7 = districts.length > 0 ? <m.div variants={variants} custom={1}><DistrictsMarquee districts={districts} isDark={isDark} /></m.div> : null;
    $[6] = districts;
    $[7] = isDark;
    $[8] = variants;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <m.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={t5}>{t6}{t7}</m.div>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== firstImage.alt || $[14] !== firstImage.height || $[15] !== firstImage.src || $[16] !== firstImage.width) {
    t9 = <div className="mr-[30%] min-w-0 flex-1"><Image src={firstImage.src} alt={firstImage.alt} width={firstImage.width} height={firstImage.height} sizes="(max-width: 767px) 60vw, 30vw" className="aspect-[2/3] w-full object-cover rounded-2xl md:rounded-3xl" /></div>;
    $[13] = firstImage.alt;
    $[14] = firstImage.height;
    $[15] = firstImage.src;
    $[16] = firstImage.width;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== secondImage.alt || $[19] !== secondImage.height || $[20] !== secondImage.src || $[21] !== secondImage.width) {
    t10 = <div className="absolute bottom-auto left-auto right-0 top-[42%] z-10 w-1/2 md:top-[48%]"><Image src={secondImage.src} alt={secondImage.alt} width={secondImage.width} height={secondImage.height} sizes="(max-width: 767px) 40vw, 20vw" className="aspect-square w-full object-cover rounded-2xl md:rounded-3xl" /></div>;
    $[18] = secondImage.alt;
    $[19] = secondImage.height;
    $[20] = secondImage.src;
    $[21] = secondImage.width;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t10 || $[24] !== t9) {
    t11 = <div className="relative flex w-full">{t9}{t10}</div>;
    $[23] = t10;
    $[24] = t9;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t11 || $[27] !== t8) {
    t12 = <div className="container"><div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-16">{t8}{t11}</div></div>;
    $[26] = t11;
    $[27] = t8;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t4) {
    t13 = <LazyMotion features={domAnimation}><section className={t4}>{t12}</section></LazyMotion>;
    $[29] = t12;
    $[30] = t4;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
}